import { Injectable, signal } from '@angular/core';
import { MarketplaceAppService, StateFilterOptions, VendorService } from '@galaxy/marketplace-apps/v1';
import { AppVendor, FieldMask } from '@vendasta/marketplace-apps/v1';
import { MarketplaceApp } from '@vendasta/marketplace-apps/v1/_internal/objects/app';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take, withLatestFrom } from 'rxjs/operators';

export interface State {
  products: ProductData[];
  searchTerm: string;
  cursor: string;
  hasMore: boolean;
}

export const defaultState: State = {
  products: [],
  searchTerm: '',
  cursor: '',
  hasMore: false,
};

export interface ListProductsResponse {
  products: MarketplaceApp[];
  cursor: string;
  hasMore: boolean;
}

export interface ProductsPageData {
  products: ProductData[];
  cursor: string;
  hasMore: boolean;
}

export interface ProductData {
  partnerId: string;
  partnerName: string;
  productId: string;
  productName: string;
  productIcon: string;
}

const RECENT_PRODUCTS_KEY = 'vcc:recentProducts';
const MAX_RECENT_PRODUCTS = 5;

@Injectable()
export class ProductSearchService {
  recentProducts = signal<ProductData[]>([]);

  constructor(private marketplaceAppService: MarketplaceAppService, private vendorService: VendorService) {
    this.loadRecentProducts();
  }

  listProductData(searchTerm: string, cursor: string, pageSize: number): Observable<ProductsPageData> {
    const resp$ = this.listProducts(searchTerm, cursor, pageSize);
    const vendorsMap$ = resp$.pipe(
      switchMap((productsResp) => {
        const { products } = productsResp;
        return this.getProductVendors(products);
      }),
    );
    return vendorsMap$.pipe(
      withLatestFrom(resp$),
      map(([vendorsMap, resp]) => {
        const { products } = resp;
        const productData = this.buildProductData(vendorsMap, products);
        return {
          products: productData,
          cursor: resp.cursor,
          hasMore: resp.hasMore,
        };
      }),
    );
  }

  private listProducts(searchTerm: string, cursor: string, pageSize: number): Observable<ListProductsResponse> {
    const fieldMask = new FieldMask({ paths: ['vendorId', 'appId', 'name', 'icon'] });
    return this.marketplaceAppService
      .listApps(
        pageSize,
        cursor,
        fieldMask,
        '',
        searchTerm,
        null,
        null,
        StateFilterOptions.STATE_FILTER_OPTIONS_STATE_ALL,
      )
      .pipe(
        map((resp) => {
          return {
            products: resp?.apps ?? [],
            cursor: resp?.cursor ?? '',
            hasMore: resp?.hasMore ?? false,
          };
        }),
        take(1),
        shareReplay(1),
      );
  }

  saveRecentProduct(productId: string): void {
    let recentProductIds = this.getRecentProductIds();

    recentProductIds = recentProductIds.filter((recentProductId) => recentProductId !== productId);
    recentProductIds.unshift(productId);

    if (recentProductIds.length > MAX_RECENT_PRODUCTS) {
      recentProductIds = recentProductIds.slice(0, MAX_RECENT_PRODUCTS);
    }
    localStorage.setItem(RECENT_PRODUCTS_KEY, JSON.stringify(recentProductIds));
  }

  private getRecentProductIds(): string[] {
    const recentProductsSerialized = localStorage.getItem(RECENT_PRODUCTS_KEY);
    let recentProducts = [];
    if (recentProductsSerialized) {
      try {
        recentProducts = JSON.parse(recentProductsSerialized);
      } catch {
        console.error('recent products data from localStorage could not be loaded');
      }
    }
    return recentProducts;
  }

  private loadRecentProducts(): void {
    const recentProducts = this.getRecentProductIds();
    if (!recentProducts.length) return;

    this.marketplaceAppService
      .getMultiApps(recentProducts)
      .pipe(
        switchMap((apps) => {
          if (!apps?.length) return of([]);
          return this.getProductVendors(apps).pipe(map((vendorsMap) => this.buildProductData(vendorsMap, apps)));
        }),
      )
      .subscribe((products) => {
        this.recentProducts.set(products);
      });
  }

  private getProductVendors(products: MarketplaceApp[]): Observable<Map<string, AppVendor>> {
    if (!products?.length) {
      return of(new Map<string, AppVendor>());
    }
    const vendorIds = Array.from(
      products.reduce((set, product) => {
        if (product?.vendorId) {
          set.add(product.vendorId);
        }
        return set;
      }, new Set<string>()),
    );
    if (!vendorIds.length) {
      return of(new Map<string, AppVendor>());
    }
    return this.vendorService.getMulti(vendorIds).pipe(
      map((vendors) => {
        const vendorsMap = new Map<string, AppVendor>();
        vendors.forEach((vendor) => {
          if (vendor) {
            vendorsMap.set(vendor.id, vendor);
          }
        });
        return vendorsMap;
      }),
      shareReplay(1),
    );
  }

  private buildProductData(vendorsMap: Map<string, AppVendor>, products: MarketplaceApp[]): ProductData[] {
    return products.map((product) => {
      const vendor = vendorsMap.get(product.vendorId);
      return {
        partnerId: vendor?.partnerId,
        partnerName: vendor?.name,
        productId: product.appId,
        productName: product.name,
        productIcon: product.icon,
      };
    });
  }
}
